import { useCallback, useEffect, useState } from 'react';
import { StagingBanner } from '../../../app/StagingBanner';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { AlertsPanel } from '../components/AlertsPanel';
import { AnnouncementsPanel } from '../components/AnnouncementsPanel';
import { DonutChart } from '../components/DonutChart';
import { CountStatCardBody, MoneyStatCardBody } from '../components/StatCard';
import { ProductOfferingsRow } from '../components/ProductOfferingsRow';
import { TopHeader } from '../components/TopHeader';
import { dashboardGateway } from '../index';
import {
  DEDUCTIONS_BREAKDOWN_COLORS, PAYROLL_BREAKDOWN_COLORS, formatCurrency,
  type DashboardSummaryResponse,
} from '../types';
import '../dashboard.css';

/**
 * The official Escochex dashboard, rebuilt to pixel-match `docs/design-reference/escochex-official-
 * dashboard.png` — see docs/dashboard-architecture.md for the full legacy-to-EPN field mapping. Rendered as
 * a standalone, shell-less route (no EPN topbar/sidebar) per the reference's own single-header layout;
 * every other EPN route keeps the normal AppShell untouched.
 */
export function DashboardPage() {
  const [summary, setSummary] = useState<DashboardSummaryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await dashboardGateway.getSummary();
      setSummary(result);
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load the dashboard.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  return (
    <div className="escochex-dashboard">
      <StagingBanner />
      {/* This inner wrapper — not the outer div above — is what the reference screenshot's own bounds
          actually cover (the banner is EPN's own global safety chrome, present on every page in this app;
          it isn't part of the Escochex reference design). The Playwright visual-regression test targets
          this element specifically for that reason — see tests/e2e/dashboard-visual.spec.ts. */}
      <div className="ed-page" data-testid="dashboard-content-root">
        <TopHeader />

        <div className="ed-body">
          {error && (
            <div className="ed-card" style={{ marginBottom: 16 }}>
              <p className="ed-section-error" style={{ padding: 0 }}>{error}</p>
              <button type="button" onClick={() => void load()}>Retry</button>
            </div>
          )}

          {loading && <DashboardSkeleton />}

          {!loading && summary && <DashboardContent summary={summary} />}

          {!loading && !summary && !error && (
            <div className="ed-card ed-empty-state">Could not load the dashboard.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <>
      <div className="ed-summary-row">
        <div className="ed-stat-grid">
          {[0, 1, 2, 3].map((i) => <div key={i} className="ed-skeleton" style={{ height: 148, borderRadius: 14 }} />)}
        </div>
        <div className="ed-skeleton" style={{ flex: 1, borderRadius: 14 }} />
        <div className="ed-skeleton" style={{ flex: 1, borderRadius: 14 }} />
      </div>
      <div className="ed-skeleton" style={{ height: 220, borderRadius: 14, marginBottom: 16 }} />
      <div className="ed-skeleton" style={{ height: 220, borderRadius: 14 }} />
    </>
  );
}

function DashboardContent({ summary }: { summary: DashboardSummaryResponse }) {
  if (!summary.hasAnyEmployees && !summary.availability.payrollUnavailable) {
    return (
      <div className="ed-card ed-empty-state" data-testid="dashboard-empty-state">
        No payroll has been processed for this company yet. Once a payroll run is approved, this dashboard
        will show your payroll cost, taxes, and breakdowns for the current month.
      </div>
    );
  }

  const payroll = summary.payrollBreakdown;
  // Net pay + employee taxes + employee deductions = gross wages; adding employer taxes gives the total employer payroll cost.
  const payrollSegments = payroll === null ? [] : [
    { label: 'Net Pay', amount: payroll.netPay, color: PAYROLL_BREAKDOWN_COLORS.netPay },
    { label: 'Employee Taxes', amount: payroll.employeeTaxes, color: PAYROLL_BREAKDOWN_COLORS.employeeTaxes },
    { label: 'Employee Deductions', amount: payroll.employeeDeductions, color: PAYROLL_BREAKDOWN_COLORS.employeeDeductions },
    { label: 'Employer Taxes', amount: payroll.employerTaxes, color: PAYROLL_BREAKDOWN_COLORS.employerTaxes },
  ];

  const deductionSegments = summary.deductionsBreakdown.categories.map((c, i) => ({
    label: c.name, amount: c.amount, color: DEDUCTIONS_BREAKDOWN_COLORS[i % DEDUCTIONS_BREAKDOWN_COLORS.length]!,
  }));

  return (
    <>
      <div className="ed-summary-row">
        <div className="ed-stat-grid">
          <MoneyStatCardBody label="Total Employer Payroll Cost" amount={summary.totalEmployerPayrollCost.amount} trend={summary.totalEmployerPayrollCost.trend} icon="💰" />
          <MoneyStatCardBody label="Total Taxes Due" amount={summary.totalTaxesDue.amount} trend={summary.totalTaxesDue.trend} icon="📄" />
          <CountStatCardBody label="Total Employees Paid" count={summary.totalEmployeesPaid.count} trend={summary.totalEmployeesPaid.trend} icon="👥" />
          <MoneyStatCardBody
            label="Net Pay" amount={summary.netPay.amount} trend={summary.netPay.trend} icon="$"
            extraHeaderContent={<span aria-hidden="true" style={{ fontSize: 10, color: '#94a3b8' }}> ▾</span>}
          />
        </div>

        <div className="ed-card ed-chart-card" data-testid="dashboard-payroll-breakdown-card">
          <div className="ed-chart-header">
            <span className="ed-chart-period">This Month</span>
            <span className="ed-icon-circle" aria-hidden="true">📊</span>
          </div>
          <h3 className="ed-chart-title">Payroll Breakdown</h3>
          {payroll !== null && <p className="ed-chart-amount">{formatCurrency(payroll.total)}</p>}
          {payroll === null ? (
            <p className="ed-donut-empty">Data unavailable: the payroll service could not be reached.</p>
          ) : payroll.total === 0 ? (
            <p className="ed-donut-empty">No payroll processed this month.</p>
          ) : (
            <div className="ed-donut-row">
              <ul className="ed-donut-legend">
                {payrollSegments.map((s) => (
                  <li key={s.label} className="ed-donut-legend-item">
                    <span className="ed-donut-legend-swatch" style={{ background: s.color }} aria-hidden="true" />
                    {s.label}
                  </li>
                ))}
              </ul>
              <DonutChart segments={payrollSegments} total={payroll.total} />
            </div>
          )}

        </div>

        <div className="ed-card ed-chart-card" data-testid="dashboard-deductions-breakdown-card">
          <div className="ed-chart-header">
            <span className="ed-chart-period">This Month</span>
            <span className="ed-icon-circle" aria-hidden="true">📄</span>
          </div>
          <h3 className="ed-chart-title">Deductions Breakdown</h3>
          <p className="ed-chart-amount">{formatCurrency(summary.deductionsBreakdown.total)}</p>
          {summary.availability.deductionsUnavailable ? (
            <p className="ed-donut-empty">Could not load right now.</p>
          ) : deductionSegments.length === 0 ? (
            <p className="ed-donut-empty">No active deductions this month.</p>
          ) : (
            <div className="ed-donut-row">
              <ul className="ed-donut-legend">
                {deductionSegments.map((s) => (
                  <li key={s.label} className="ed-donut-legend-item">
                    <span className="ed-donut-legend-swatch" style={{ background: s.color }} aria-hidden="true" />
                    {s.label}
                  </li>
                ))}
              </ul>
              <DonutChart segments={deductionSegments} total={summary.deductionsBreakdown.total} />
            </div>
          )}
        </div>
      </div>

      <div className="ed-section-row">
        <div className="ed-section-col">
          <ProductOfferingsRow />
          <AnnouncementsPanel announcements={summary.announcements} />
        </div>
        <div className="ed-section-col">
          <AlertsPanel alerts={summary.alerts} unavailable={summary.availability.taxesUnavailable} />
        </div>
      </div>
    </>
  );
}
